/**
 * @category Tutorial
 * @module TutorialContentRenderer
 * @description Utilities for rendering tutorial content from JSON definitions
 */

import React from 'react';
import { MathJaxContext } from 'better-react-mathjax';
import { EXTRA_INFO_TEMPLATES, INSTRUCTIONS_TEMPLATES } from '../tutorialContent/tutorialTemplates';

/**
 * Render tutorial content from JSON step definition
 * @param {Object} stepContent - Step content object from JSON
 * @param {string} stepContent.text - Main text content
 * @param {Array<string>} stepContent.instructions - Array of instruction strings
 * @param {string} stepContent.extraInfo - Additional information text
 * @returns {JSX.Element} Rendered tutorial content
 */
export const renderTutorialContent = (stepContent) => {
  if (!stepContent) return null;

  const { text, instructions = [], extraInfo, instructionsId } = stepContent;

  return (
    <MathJaxContext>
      <div className="tutorial-welcome-modal-tutorial-text">
        {text && <div>{processMarkdown(text)}</div>}
        {instructions.length > 0 && (
          <div>
          <ol>
            {instructions.map((instruction, index) => (
              <li key={index}>
                {processMarkdown(instruction)}
              </li>
            ))}
          </ol>
          </div>
        )}
        {instructionsId && <div>{INSTRUCTIONS_TEMPLATES[instructionsId]}</div>}
        {extraInfo && (
          <div>
            {extraInfo.contentId && <div>{EXTRA_INFO_TEMPLATES[extraInfo.contentId]}</div>}
            {extraInfo.title && <b>{extraInfo.title}</b>}
            {extraInfo.text && extraInfo.text.map((text, index) => (
              <div key={index}>{processMarkdown(text)}</div>
            ))}
          </div>
        )}
      </div>
    </MathJaxContext>
  );
};

/**
 * Simple markdown processor for bold text and superscript
 * Converts **text** to <b>text</b> and ^text^ to <sup>text</sup>
 * @param {string} text - Text with markdown formatting
 * @returns {JSX.Element|string} Processed text with JSX elements
 */
const processMarkdown = (text) => {
  if (typeof text !== 'string') return text;
  
  // First handle bold (**text**)
  const parts = text.split(/(\*\*.*?\*\*)/g);
  
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      // Remove ** markers and make bold, then process superscript inside
      const boldText = part.slice(2, -2);
      const processedBoldText = processSuperscript(boldText);
      return <b key={index}>{processedBoldText}</b>;
    }
    // Process superscript in non-bold parts
    return processSuperscript(part);
  });
};

// Helper function to process superscript
const processSuperscript = (text) => {
  if (typeof text !== 'string') return text;
  
  const superscriptRegex = /\^([^^]+)\^/g;
  const processedText = text.replace(superscriptRegex, '<sup>$1</sup>');
  
  // If it contains superscript tags, render as HTML
  if (processedText.includes('<sup>')) {
    return <span dangerouslySetInnerHTML={{ __html: processedText }} />;
  }
  
  return processedText;
};

/**
 * Get tutorial content for current step from loaded definition
 * @param {Object} tutorialDefinition - Loaded tutorial definition
 * @param {number} stepNumber - Current step number
 * @returns {JSX.Element|null} Rendered content or null if not found
 */
export const getCurrentTutorialContent = (tutorialDefinition, stepNumber) => {
  if (!tutorialDefinition || !stepNumber) return null;
  
  const currentStep = tutorialDefinition.stepMap[stepNumber];
  if (!currentStep || !currentStep.content) return null;
  
  return renderTutorialContent(currentStep.content);
};

/**
 * Get tutorial title for current step
 * @param {Object} tutorialDefinition - Loaded tutorial definition  
 * @param {number} stepNumber - Current step number
 * @returns {JSX.Element|string|null} Processed step title or null if not found
 */
export const getCurrentTutorialTitle = (tutorialDefinition, stepNumber) => {
  if (!tutorialDefinition || !stepNumber) return null;
  
  const currentStep = tutorialDefinition.stepMap[stepNumber];
  const title = currentStep?.title;
  
  if (!title) return null;
  
  // Process the title through markdown to handle superscript and bold
  return processMarkdown(title);
};

/**
 * Get tutorial help content for current step
 * @param {Object} tutorialDefinition - Loaded tutorial definition  
 * @param {number} stepNumber - Current step number
 * @returns {JSX.Element|string|null} Processed help content or null if not found
 */
export const getCurrentTutorialHelp = (tutorialDefinition, stepNumber) => {
  if (!tutorialDefinition || !stepNumber) return null;
  
  const currentStep = tutorialDefinition.stepMap[stepNumber];
  const help = currentStep?.help;
  
  if (!help) return null;
  
  // Process the help content through markdown to handle superscript and bold
  return processMarkdown(help);
};

/**
 * Compatibility function to mimic the old tutorial1Content format
 * @param {Object} tutorialDefinition - Loaded tutorial definition
 * @returns {Object} Object with step numbers as keys, compatible with old system
 */
// export const createCompatibilityContent = (tutorialDefinition) => {
//   if (!tutorialDefinition) return {};
  
//   return tutorialDefinition.steps.reduce((compat, step) => {
//     compat[step.stepNumber] = {
//       title: step.title,
//       content: renderTutorialContent(step.content)
//     };
//     return compat;
//   }, {});
// };

export default {
  renderTutorialContent,
  getCurrentTutorialContent,
  getCurrentTutorialTitle,
  getCurrentTutorialHelp,
  //createCompatibilityContent,
};
